import { useEffect, useMemo, useState } from "react";
import {
  IBookingCreationAppointmentNotes,
  IBookingCreationBody,
  IBookingRechseduleServiceDetails,
  IBookingRechseduleUserId,
  IBookingRechseduleUserSlotId,
  IBookingRescheduleBody,
  IBookingUpdateModuleType,
} from "@/api_framework/api_modals/Bookings";
import { useBookingCreationAPI } from "@/app/hooks/api_hooks/bookings/useBookingCreationAPI";
import { surgeSlotPayload, useBookingCartPreviewAPI } from "@/app/hooks/api_hooks/bookings/useBookingCartPreviewAPI";
import { useBookingRescheduleAndCancelAPI } from "@/app/hooks/api_hooks/bookings/useBookingRescheduleAndCancelationAPI";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import { useMappedConstants } from "@/app/hooks/useMappedConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { FRONT_DESK_OB_LOG_SOURCE } from "../FrontDeskOverbookingComponent/helpers";
import { useUtils } from "@/app/hooks/useUtils";
import { buildBookingServiceMaps, resolveBookingServiceSelection, buildVaccineCartItems, buildDiagnosticCartItems, getPreviewVaccineItems, resolveDiagnosticCartItems, isVaccinationServiceType } from "@/app/utils/bookingServiceSelection";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ISlotsState, setSelectedPurchaseMembershipPayAtClinic } from "@/app_redux/reducers/slices/auth/slots_slice";

import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import CommentSectionModal from "@/app/components/routes/auth_routes/SlotsScreen/CommentSection";
import SendWhatsappScriptimport from "@/app/components/routes/auth_routes/SlotsScreen/SendWhatsappScript";
import { useIsBookingChargableAPI } from "@/app/hooks/api_hooks/bookings/useIsBookingChargableAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { normalizePaymentStatus, resolvePaymentDisplayStatus } from "@/app/components/routes/auth_routes/SlotsScreen/ComfirmationComponent/utils/confirmationHelpers";
import CommentsCard from "@/app/components/routes/auth_routes/SlotsScreen/ComfirmationComponent/components/CommentsCard";
import PricingSummaryCard from "@/app/components/routes/auth_routes/SlotsScreen/ComfirmationComponent/components/PricingSummaryCard";
import AdvancePaymentCard from "@/app/components/routes/auth_routes/SlotsScreen/ComfirmationComponent/components/AdvancePaymentCard";
import AppointmentDetailsCard from "@/app/components/routes/auth_routes/SlotsScreen/ComfirmationComponent/components/AppointmentDetailsCard";
import CartItemsCard from "@/app/components/routes/auth_routes/SlotsScreen/ComfirmationComponent/components/CartItemsCard";
import { HOME_SERVICE_ID } from "@/constants";

const SlotsConfirmationComponent = (_props: any) => {
  const [params, _setParams] = useSearchParams();
  const { VMSSourceId, bookingTypeNormal, bookingTypeOverbooking, bookingTypeLate } = useStringConstants();
  // Front desk books only via the direct overbooking flow
  const isFrontDeskOverbooking = useFeatureAccess("FRONT_DESK_OVERBOOKING");
  const { appointmentNotesTags } = useMappedConstants();
  const { getRouteKey } = useRouter();
  const { addAMPMToTime, ISODatetoHuman, convertDataToHTMLList } = useUtils();
  const navigate = useNavigate();
  const [bookingCreated, setBookingCreated] = useState<boolean>(false);
  const [bookingUpdated, setBookingUpdated] = useState<boolean>(false);
  const [completedBookingFlow, setCompleteBookingFlow] = useState<boolean>(false);
  const [disableBookingCreationButton, setDisableBookingCreationButton] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { newBookingId, prepaidPayment, execute: createBooking } = useBookingCreationAPI();
  const { execute: fetchBookingCartPreview } = useBookingCartPreviewAPI();
  const { updatedBookingId, execute: updateBooking } = useBookingRescheduleAndCancelAPI();
  const { serviceTypes, execute: fetchOrgServicesByFilter } = useOrgServicesByFilterAPI();
  const { execute: fetchBookingStatus, bookingDetails } = useIsBookingChargableAPI();
  const CPRouteID = getRouteKey("CUSTOMER_PROFILE", "id");
  const slotsRouteID = getRouteKey("SLOTS", "id");
  const { routeState: SRS } = useRouteInfo(slotsRouteID);

  const {
    filters,
    selectedSlotDetails,
    selectedSlotsClinicDetails,
    selectedSlotStaffDetails,
    atHomeSelectedDoctorId,
    address,
    commentsData,
    couponsData,
    lateBookingFlag,
    lateBookingStartTime,
    rescheduleFlag,
    rescheduledBooking,
    overbookingFlag,
    purchaseOptionsData,
    bookingCartPreviewData,
    selectedPurchaseServiceId,
    selectedPurchaseAddOnIds,
    selectedPurchaseVaccineIds,
    selectedVaccineCouponMap,
    selectedAddOnCouponMap,
    selectedPurchaseMembershipId,
    selectedPurchaseMembershipPayAtClinic,
    selectedMembershipCouponId,
    selectedMembershipCouponName,
    selectedSurgeryIds,
    selectedSurgeryCouponMap,
    selectedPurchaseOtherTestIds,
    rescheduleBookingSummaryData
  } = SRS.state as ISlotsState;

  const { routeState: CPRS } = useRouteInfo(CPRouteID);

  const { petDetails, customerDetails, customerId, petId, customerAddresses } = CPRS.state as ICustomerProfileState;

  const [open, setOpen] = useState<boolean>(false);
  const [isBookingOnHold, setIsBookingOnHold] = useState<boolean>(false);
  const [refreshLoader, setRefreshLoader] = useState<boolean>(false);
  const [editCartModalSignal, setEditCartModalSignal] = useState<number>(0);
  const membershipPayAtClinicSelected = Boolean(selectedPurchaseMembershipPayAtClinic);

  const { execute: logClick } = useClickLogAPI();
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);
  // Booking created from the front desk direct overbooking flow (logs are tagged with its source)
  const isFrontDeskOverbookingFlow = isFrontDeskOverbooking && Boolean(overbookingFlag) && !rescheduleFlag;
  const logPreviewEvent = (event_type: string, extra: Partial<IClickLogBody> = {}) => {
    logClick({
      event_type,
      ...(isFrontDeskOverbookingFlow ? { source: FRONT_DESK_OB_LOG_SOURCE } : {}),
      scr_id: "BOOKING_PREVIEW",
      client_id: customerId,
      pet_id: petId,
      vms_user_id: vmsUserId,
      clinic_id: selectedSlotsClinicDetails?.clinic_id,
      clinic_name: selectedSlotsClinicDetails?.clinic_name,
      staff_id: selectedSlotStaffDetails?.staff_id,
      timestamp: Date.now(),
      ...extra,
    });
  };

  const handelRefesh = () => {
    setRefreshLoader(true);
    fetchBookingStatus(rescheduleFlag ? updatedBookingId : newBookingId).then(({ status }) => {
      if (status) {
        setRefreshLoader(false);
      } else {
        setRefreshLoader(false);
      }
    });
  };

  useEffect(() => {
    const rbi = params.get("rbi");
    fetchBookingStatus(rbi);
  }, [params.get("rbi")]);

  useEffect(() => {
    fetchOrgServicesByFilter({ filterType: "booking_vms" });
  }, []);

  const bookingServiceMaps = useMemo(() => buildBookingServiceMaps(serviceTypes ?? []), [serviceTypes]);
  const selectedServiceSelection = useMemo(
    () =>
      resolveBookingServiceSelection({
        maps: bookingServiceMaps,
        selectedServiceKey: filters?.service_id,
        selectedSubServiceId: filters?.sub_service_id,
      }),
    [bookingServiceMaps, filters?.service_id, filters?.sub_service_id],
  );
  const selectedSubService = selectedServiceSelection.selectedSubService;
  const selectedServiceTypeName = selectedServiceSelection.selectedServiceTypeName;
  const selectedServiceTypeId = selectedServiceSelection.selectedServiceTypeId;
  const selectedServiceIdPayload = selectedServiceSelection.selectedServiceIdPayload;
  const isAtHomeServiceSelected = selectedServiceTypeId === HOME_SERVICE_ID;

  const parseCoordinate = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  const getAtHomeLocationPayload = () => {
    if (!isAtHomeServiceSelected) {
      return {};
    }

    const resolvedLatitude = parseCoordinate((address as any)?.state?.lat ?? filters?.location_details?.latitude);
    const resolvedLongitude = parseCoordinate((address as any)?.state?.long ?? filters?.location_details?.longitude);
    const resolvedAddress =
      (address as any)?.state?.formattedAddress ?? (address as any)?.title ?? (address as any)?.formatted_address ?? "";
    const resolvedAddressId = (address as any)?.id ?? (address as any)?.address_id ?? null;
    return {
      latitude: resolvedLatitude,
      longitude: resolvedLongitude,
      address: resolvedAddress,
      address_id: resolvedAddressId,
    };
  };
  const previewServiceItem = useMemo(
    () => bookingCartPreviewData?.cart_items?.find((item) => item.type === "service") || null,
    [bookingCartPreviewData],
  );
  const previewMembershipItem = useMemo(
    () => bookingCartPreviewData?.cart_items?.find((item) => item.type === "membership") || null,
    [bookingCartPreviewData],
  );
  const previewAddOnItems = useMemo(
    () =>
      bookingCartPreviewData?.cart_items?.filter(
        (item) =>
          item.type === "service_add_ons" ||
          item.type === "add_on" ||
          item.type === "add_on_service" ||
          item.type === "add_on_bom",
      ) || [],
    [bookingCartPreviewData],
  );
  const summaryAddOnItems = useMemo(
    () => {
      const services = rescheduleBookingSummaryData?.addons?.add_on_services ?? [];
      const boms = rescheduleBookingSummaryData?.addons?.add_on_boms ?? [];
      return [...services, ...boms].map((addOn) => ({
        id: addOn.id,
        type: addOn.type || "service_add_ons",
        qty: 1,
        name: addOn.name,
        price: Number(addOn.price ?? 0),
        discount: Number(addOn.discount ?? 0),
        final_price: Number(addOn.final_price ?? addOn.price ?? 0),
        discount_percent: Number(addOn.discount_percent ?? 0),
        duration: addOn.duration ?? null,
        description: addOn.description ?? null,
        image_url: (addOn.image_url || "").replace(/^\{|\}$/g, ""),
      }));
    },
    [rescheduleBookingSummaryData],
  );
  const summaryMembershipItem = useMemo(() => {
    if (!selectedPurchaseMembershipId) return null;
    const summaryMembership =
      (rescheduleBookingSummaryData?.addons?.memberships ?? []).find(
        (membership) => membership.id === selectedPurchaseMembershipId,
      ) ?? null;
    if (!summaryMembership) {
      return null;
    }
    return {
      ...summaryMembership,
      type: "membership",
      qty: 1,
      details: Array.isArray(summaryMembership.details)
        ? summaryMembership.details.join(", ")
        : summaryMembership.details ?? null,
    };
  }, [rescheduleBookingSummaryData, selectedPurchaseMembershipId]);
  const selectedMembershipItem = useMemo(
    () => {
      if (!selectedPurchaseMembershipId) {
        return null;
      }
      const selectedPreviewMembership =
        previewMembershipItem?.id === selectedPurchaseMembershipId ? previewMembershipItem : null;
      return (
        selectedPreviewMembership ||
        purchaseOptionsData?.memberships?.find((membership) => membership.id === selectedPurchaseMembershipId) ||
        summaryMembershipItem ||
        null
      );
    },
    [previewMembershipItem, purchaseOptionsData, selectedPurchaseMembershipId, summaryMembershipItem],
  );
  const selectedAddOnItems = useMemo(() => {
    const selectedAddOnIds = selectedPurchaseAddOnIds ?? [];
    if (selectedAddOnIds.length === 0) {
      return [];
    }

    const previewAddOnItemsMap = new Map(previewAddOnItems.map((item) => [item.id, item]));
    const purchaseOptionsAddOnMap = new Map((purchaseOptionsData?.service_add_ons ?? []).map((item) => [item.id, item]));
    const summaryAddOnItemsMap = new Map(summaryAddOnItems.map((item) => [item.id, item]));
    return selectedAddOnIds
      .map(
        (addOnId) =>
          previewAddOnItemsMap.get(addOnId) ||
          purchaseOptionsAddOnMap.get(addOnId) ||
          summaryAddOnItemsMap.get(addOnId) ||
          null,
      )
      .filter((item) => Boolean(item));
  }, [previewAddOnItems, summaryAddOnItems, purchaseOptionsData, selectedPurchaseAddOnIds]);
  const selectedVaccineDisplayItems = useMemo(() => {
    const selectedIds = new Set(selectedPurchaseVaccineIds ?? []);
    return getPreviewVaccineItems(bookingCartPreviewData?.cart_items).filter((vaccine) =>
      selectedIds.has(vaccine.vaccine_id || vaccine.id),
    );
  }, [bookingCartPreviewData, selectedPurchaseVaccineIds]);

  // Diagnostics ride along with the booking (lab/EMR flows) — VMS only displays and preserves them.
  const selectedDiagnosticItems = useMemo(
    () =>
      resolveDiagnosticCartItems({
        previewCartItems: bookingCartPreviewData?.cart_items,
        summaryData: rescheduleFlag ? rescheduleBookingSummaryData : null,
      }),
    [bookingCartPreviewData, rescheduleBookingSummaryData, rescheduleFlag],
  );

  const selectedSurgeryItems = useMemo(
    () =>
      (bookingCartPreviewData?.cart_items ?? []).filter((item) => (selectedSurgeryIds ?? []).includes(item.id)),
    [bookingCartPreviewData, selectedSurgeryIds],
  );
  const surgeryCartItems = useMemo(
    () =>
      selectedSurgeryItems.map((item) => {
        const coupon = selectedSurgeryCouponMap?.[item.id];
        return {
          type: item.type,
          id: item.id,
          qty: 1,
          ...(coupon?.couponId ? { coupon_id: coupon.couponId } : {}),
          ...(coupon?.couponName ? { coupon_name: coupon.couponName } : {}),
        };
      }),
    [selectedSurgeryItems, selectedSurgeryCouponMap],
  );
  const serviceDisplayItem = useMemo(
    () =>
      previewServiceItem ||
      purchaseOptionsData?.services?.[0] || {
        id: filters?.sub_service_id || "service",
        name: selectedSubService?.sub_service_name || selectedServiceTypeName || "Service",
        description: selectedServiceTypeName || "Service",
        price: Number(selectedSlotDetails?.booking_fee ?? 0),
        final_price: Number(selectedSlotDetails?.booking_fee ?? 0),
        type: "service",
      },
    [
      previewServiceItem,
      purchaseOptionsData?.services,
      filters?.sub_service_id,
      selectedSubService?.sub_service_name,
      selectedServiceTypeName,
      selectedSlotDetails?.booking_fee,
    ],
  );
  const summaryService = rescheduleFlag ? rescheduleBookingSummaryData?.service : null;
  const serviceOfferCallout = summaryService?.gift_id ? summaryService.offer_callout : null;
  const billingDetails = bookingCartPreviewData?.billing_details;
  const upsellScript = bookingCartPreviewData?.upsell_script;
  const normalizedUpsellScript =
    typeof upsellScript === "string" ? upsellScript.replace(/\\n|\/n/g, "\n") : "";
  const bookingFeeChip = billingDetails?.booking_fee_chip;
  const bookingFeeChipText = bookingFeeChip?.text;
  const billingSummary = {
    totalPrice: billingDetails?.total_price,
    totalDiscount: billingDetails?.total_discount,
    totalAmountToPay: billingDetails?.total_amount_to_pay,
    amountToPayAtClinic: billingDetails?.amount_to_pay_at_clinic,
    amountToPayNow: billingDetails?.amount_to_pay_now,
  };
  const selectedMembershipOriginalPrice = Number(selectedMembershipItem?.price ?? 0);
  const selectedMembershipFinalPrice = Number(selectedMembershipItem?.final_price ?? selectedMembershipOriginalPrice);
  const membershipDiscountPercent = Number(selectedMembershipItem?.discount_percent ?? 0);
  const selectedCouponNameFromDropdown =
    commentsData?.coupon ||
    couponsData?.find((coupon) => coupon?.coupon_id === commentsData?.couponId)?.coupon_name ||
    "";
  const resolvedDiscountCouponLabel = selectedCouponNameFromDropdown || "No Discount";
  const selectedCouponIdForPreview =
    commentsData?.couponId ||
    couponsData?.find((coupon) => coupon?.coupon_name === commentsData?.coupon)?.coupon_id ||
    undefined;
  const rescheduleBookingIdFromParams = params.get("rbi");
  const additionalCommentsList = useMemo(() => {
    const notes: string[] = [];
    const freeText = commentsData?.otherComment || "";
    const parsed = freeText
      .split("•")
      .map((line) => line.trim())
      .filter((line) => Boolean(line));
    notes.push(...parsed);
    if (notes.length === 0) {
      notes.push("No additional comments");
    }
    return notes;
  }, [commentsData?.membershipName, commentsData?.otherComment]);

  useEffect(() => {
    if (bookingDetails?.booking_status?.toLowerCase() === "upcoming") {
      setIsBookingOnHold(true);
    } else {
      setIsBookingOnHold(false);
    }
  }, [bookingDetails]);

  const constructAppointmentNotes = (): IBookingCreationAppointmentNotes[] => {
    const allowedCommentKeys = new Set(["coupon", "vaccination", "otherComment"]);
    const resolveCommentKey = (key: string) => {
      if (commentsData[key] === "" || commentsData[key] === undefined) {
        return null;
      } else if (commentsData[key] === "Not Applicable") {
        return " NA";
      } else if (commentsData[key] === true) {
        return "Yes";
      } else {
        return commentsData[key];
      }
    };
    const appointmentNotes: IBookingCreationAppointmentNotes[] = [];
    if (Object.keys(commentsData)?.length > 0) {
      Object.keys(commentsData).forEach((commentKey) => {
        if (!allowedCommentKeys.has(commentKey)) return;
        const resolvedValue = resolveCommentKey(commentKey);
        if (resolvedValue !== null && appointmentNotesTags[commentKey]) {
          appointmentNotes.push({
            value: `${appointmentNotesTags[commentKey]}:${resolvedValue}`,
          });
        }
      });
    }
    const isVaccine = isVaccinationServiceType(filters?.service_id);
    const selectedVaccineIds = selectedPurchaseVaccineIds ?? [];

    if (isVaccine && selectedVaccineCouponMap && selectedVaccineIds.length > 0) {
      const previewVaccineItems = (bookingCartPreviewData?.cart_items ?? []).filter(
        (item) => item.type === "vaccine"
      );
      const vaccineNotes: string[] = [];
      Object.entries(selectedVaccineCouponMap).forEach(([vaccineId, couponId]) => {
        if (couponId && selectedVaccineIds.includes(vaccineId)) {
          const vaccineItem = previewVaccineItems.find(
            (item) => (item.vaccine_id || item.id) === vaccineId
          );
          const vaccineName = vaccineItem?.vaccine_name || vaccineItem?.name || "Vaccine";
          const matchingCoupon = vaccineItem?.applicable_coupons?.find(
            (coupon) => coupon.coupon_id === couponId
          );
          const couponName = matchingCoupon?.coupon_name || couponId;
          vaccineNotes.push(`${vaccineName}:${couponName}`);
        }
      });

      if (vaccineNotes.length > 0) {
        appointmentNotes.push({
          value: "Discount Coupon : ",
        });
        vaccineNotes.forEach((noteVal) => {
          appointmentNotes.push({
            value: noteVal,
          });
        });
      }
    }
    return appointmentNotes;
  };

  const constructServiceDetails = (): IBookingRechseduleServiceDetails => {
    return {
      service_type_id: selectedServiceTypeId,
      service_id: selectedServiceIdPayload,
      service_name: selectedServiceTypeName ?? "",
      sub_service_id: selectedSubService?.sub_service_id ?? "",
      sub_service_name: selectedSubService?.sub_service_name ?? "",
    };
  };

  const constructFieldsFromFilters = () => {
    return {
      booking_date: filters?.booking_date,
      booking_start_date_time: `${filters?.booking_date} ${selectedSlotDetails?.start_time}`,
      booking_end_date_time: `${filters?.booking_date} ${selectedSlotDetails?.end_time}`,
    };
  };

  const constructUserDetails = (): IBookingRechseduleUserId => {
    return {
      id: rescheduledBooking?.user_id,
    };
  };

  const constructStartAndEndTime = () => {
    return {
      end_time: selectedSlotDetails?.end_time,
      start_time: selectedSlotDetails?.start_time,
    };
  };

  const constructSlotDetails = (): IBookingRechseduleUserSlotId => {
    return {
      slot_id: selectedSlotDetails?.slot_id,
    };
  };

  const constructStaffDetails = (): IBookingRechseduleUserId => {
    return {
      id: selectedSlotStaffDetails?.staff_id,
    };
  };

  const getSelectedProviderTag = (): string | undefined => {
    return selectedSlotStaffDetails?.tags
      ?.slice()
      ?.sort((a, b) => (a?.priority ?? 0) - (b?.priority ?? 0))?.[0]?.tag_text;
  };

  const getSelectedAddressId = (): string | undefined => {
    const selectedAddressId = (address as any)?.id;
    return selectedAddressId || undefined;
  };

  const getSelectedCustomerAddress = () => {
    const selectedAddressId = getSelectedAddressId();
    const matchedAddress = (customerAddresses ?? []).find(
      (customerAddress) => customerAddress?.address_id === selectedAddressId,
    );
    if (matchedAddress) return matchedAddress;

    if (selectedAddressId) {
      return {
        address_id: selectedAddressId,
        address_1: (address as any)?.address_1 || "",
        address_2: (address as any)?.address_2 || (address as any)?.state?.formattedAddress || (address as any)?.title || "",
        city: (address as any)?.state?.city || "",
        state: (address as any)?.state?.state || "",
        pincode: (address as any)?.state?.pincode || "",
        label: (address as any)?.label || "Home",
      };
    }

    return (customerAddresses ?? [])[0];
  };

  const buildAddressPayload = (): string | undefined => {
    const selectedCustomerAddress = getSelectedCustomerAddress();

    if (!selectedCustomerAddress) return undefined;

    const composedAddress = [
      selectedCustomerAddress?.address_1?.trim(),
      selectedCustomerAddress?.address_2?.trim(),
      selectedCustomerAddress?.city?.trim(),
      selectedCustomerAddress?.state?.trim(),
      selectedCustomerAddress?.pincode?.trim(),
    ]
      .filter(Boolean)
      .join(", ");

    return composedAddress || undefined;
  };

  const buildAddressIdPayload = (): string | undefined => {
    return getSelectedAddressId() || getSelectedCustomerAddress()?.address_id;
  };

  const constructPartlyPayloadBody = () => {
    const serviceDetails = constructServiceDetails();
    const userDetails = constructUserDetails();
    const filterFields = constructFieldsFromFilters();
    const startAndEndTime = constructStartAndEndTime();
    const slotDetails = constructSlotDetails();
    const staffDetails = constructStaffDetails();
    return {
      service_details: {
        ...serviceDetails,
      },
      user_details: {
        ...userDetails,
      },
      ...startAndEndTime,
      slot_details: {
        ...slotDetails,
      },
      ...filterFields,
      staff_details: {
        ...staffDetails,
      },
    };
  };
  const constructPartlyPayloadBodyOverBookingReshedule = () => {
    const serviceDetails = constructServiceDetails();
    const userDetails = constructUserDetails();
    const filterFields = constructFieldsFromFilters();
    const startAndEndTime = constructStartAndEndTime();

    const staffDetails = constructStaffDetails();
    return {
      service_details: {
        ...serviceDetails,
      },
      user_details: {
        ...userDetails,
      },
      ...startAndEndTime,
      ...filterFields,
      staff_details: {
        ...staffDetails,
      },
    };
  };

  const buildCartItemsPayload = ({ payAtClinic, forCreate }: { payAtClinic: boolean; forCreate: boolean }) => {
    const serviceCartItemId =
      previewServiceItem?.id ||
      selectedPurchaseServiceId ||
      purchaseOptionsData?.services?.[0]?.id ||
      filters?.sub_service_id;
    const serviceCartItems = serviceCartItemId
      ? [
        {
          type: "service",
          id: serviceCartItemId,
          qty: 1,
          coupon_id: selectedCouponIdForPreview,
        },
      ]
      : [];
    const addOnCartItems = (selectedAddOnItems ?? []).map((addOn) => {
      const addOnCouponId = selectedAddOnCouponMap?.[addOn.id];
      return {
        type: addOn?.type === "add_on_bom" ? "add_on_bom" : "add_on_service",
        id: addOn.id,
        qty: 1,
        ...(addOnCouponId ? { coupon_id: addOnCouponId } : {}),
      };
    });
    const membershipCartItems = selectedMembershipItem?.id
      ? [
        {
          type: "membership",
          id: selectedMembershipItem.id,
          qty: 1,
          pay_at_clinic: payAtClinic,
          ...(selectedMembershipCouponId ? { coupon_id: selectedMembershipCouponId } : {}),
          ...(selectedMembershipCouponName ? { coupon_name: selectedMembershipCouponName } : {}),
        },
      ]
      : [];
    const vaccineCartItems = buildVaccineCartItems({
      previewVaccineItems: getPreviewVaccineItems(bookingCartPreviewData?.cart_items),
      selectedVaccineIds: selectedPurchaseVaccineIds ?? [],
      vaccineCouponMap: selectedVaccineCouponMap,
      forCreate,
    });
    const otherTestCartItems = (purchaseOptionsData?.other_diagnostic_test ?? [])
      .filter((item) => (selectedPurchaseOtherTestIds ?? []).includes(item.id))
      .map((item) => ({ type: "other_diagnostic_test", id: item.id, qty: 1 }));
    const otherTestIds = new Set(otherTestCartItems.map((item) => item.id));
    // The preview echoes other tests back as "diagnostic" lines; drop those so each test is sent once.
    const diagnosticCartItems = buildDiagnosticCartItems({
      previewCartItems: bookingCartPreviewData?.cart_items,
      summaryData: rescheduleFlag ? rescheduleBookingSummaryData : null,
    }).filter((item) => !otherTestIds.has(item.id));
    return [
      ...serviceCartItems,
      ...addOnCartItems,
      ...vaccineCartItems,
      ...diagnosticCartItems,
      ...otherTestCartItems,
      ...membershipCartItems,
      ...surgeryCartItems,
    ];
  };

  const constructBookingUpdateBody = (): IBookingRescheduleBody => {
    const appointmentNotes = constructAppointmentNotes();
    const isAtHomeService = selectedServiceTypeId === HOME_SERVICE_ID;
    const addressPayload = buildAddressPayload();
    const addressIdPayload = buildAddressIdPayload();
    const latitude = filters?.location_details?.latitude ? Number(filters?.location_details?.latitude) : null;
    const longitude = filters?.location_details?.longitude ? Number(filters?.location_details?.longitude) : null;
    const editedComment = convertDataToHTMLList(appointmentNotes);
    const cartItems = buildCartItemsPayload({ payAtClinic: membershipPayAtClinicSelected, forCreate: true });

    const partPayloadBody = overbookingFlag
      ? constructPartlyPayloadBodyOverBookingReshedule()
      : constructPartlyPayloadBody();

    return {
      booking_status: rescheduledBooking?.booking_status,
      configuration_id: selectedSlotDetails?.configuration_id,
      clinic_details: { id: selectedSlotsClinicDetails?.clinic_id },
      comments: editedComment,
      address: addressPayload,
      address_id: addressIdPayload,
      ...(isAtHomeService
        ? {
          latitude: Number.isFinite(latitude) ? latitude : null,
          longitude: Number.isFinite(longitude) ? longitude : null,
          reason: commentsData?.reason,
        }
        : {}),
      booking_type: overbookingFlag ? "over_booking" : null,
      slot_type: selectedSlotDetails?.prepaid_booking_slot?.toLowerCase() === "yes" ? "paid" : "free",
      pet_details: rescheduledBooking?.pet_details,
      ...(isAtHomeService ? {} : { provider_tag: getSelectedProviderTag() }),
      ...getAtHomeLocationPayload(),
      ...partPayloadBody,
      cart_details: {
        cart_items: cartItems,
      },
    };
  };

  const constructBookingCreationBody = (isOverbooking = false): IBookingCreationBody => {
    const appointmentNotes = constructAppointmentNotes();
    const isAtHomeService = selectedServiceTypeId === HOME_SERVICE_ID;
    const addressPayload = buildAddressPayload();
    const addressIdPayload = buildAddressIdPayload();
    const latitude = parseCoordinate((address as any)?.state?.lat ?? filters?.location_details?.latitude);
    const longitude = parseCoordinate((address as any)?.state?.long ?? filters?.location_details?.longitude);
    const cartItems = buildCartItemsPayload({ payAtClinic: membershipPayAtClinicSelected, forCreate: true });

    const payload: IBookingCreationBody = {
      appointment_note: appointmentNotes,
      booking_date: filters?.booking_date,
      clinic_details: {
        id: selectedSlotsClinicDetails?.clinic_id,
      },
      booking_type: "",
      slot_type: selectedSlotDetails?.prepaid_booking_slot?.toLowerCase() === "yes" ? "paid" : "free",
      configuration_id: selectedSlotDetails?.configuration_id,
      end_time: selectedSlotDetails?.end_time,
      start_time: selectedSlotDetails?.start_time,
      pet_details: {
        id: petId,
      },
      source_id: VMSSourceId,
      service_details: {
        service_type_id: selectedServiceTypeId,
        service_id: selectedServiceIdPayload,
        service_name: selectedServiceTypeName ?? "",
        sub_service_id: selectedSubService?.sub_service_id ?? "",
        sub_service_name: selectedSubService?.sub_service_name ?? "",
      },
      staff_details: {
        id: selectedSlotStaffDetails?.staff_id,
      },
      ...(isAtHomeService ? {} : { provider_tag: getSelectedProviderTag() }),
      user_details: {
        id: customerId,
      },
      booking_start_date_time: `${filters?.booking_date} ${selectedSlotDetails?.start_time}`,
      booking_end_date_time: `${filters?.booking_date} ${selectedSlotDetails?.end_time}`,
      cart_details: {
        cart_items: cartItems,
      },
      ...(isAtHomeService
        ? {
          latitude: Number.isFinite(latitude) ? latitude : null,
          longitude: Number.isFinite(longitude) ? longitude : null,
          address: addressPayload,
          address_id: addressIdPayload,
          comments: appointmentNotes,
          reason: commentsData?.reason ?? "",
          /* True when the booking is made against a specific doctor chip */
          last_visited_selected: Boolean(atHomeSelectedDoctorId),
        }
        : {}),
    };
    return !isOverbooking
      ? lateBookingFlag
        ? {
          ...payload,
          booking_type: bookingTypeLate,
          start_time: lateBookingStartTime,
          booking_end_date_time: null,
          end_time: null,
          slot_details: {
            slot_id: selectedSlotDetails?.slot_id,
          },
          booking_start_date_time: `${filters?.booking_date} ${lateBookingStartTime}`,
        }
        : {
          ...payload,
          slot_details: {
            slot_id: selectedSlotDetails?.slot_id,
          },
          booking_type: bookingTypeNormal,
        }
      : {
        ...payload,
        booking_type: bookingTypeOverbooking,
        ...(isFrontDeskOverbooking ? { CM_overbooking: true } : {}),
      };
  };

  const handelConfirmation = () => {
    logPreviewEvent("CONFIRM_BOOKING_CLICK", {
      cta_text: rescheduleFlag
        ? "Reschedule Booking"
        : billingSummary.amountToPayNow > 0
          ? "Pay & Confirm Booking"
          : "Confirm Booking",
      filter_values: {
        total_amount_to_pay: billingSummary.totalAmountToPay,
        amount_to_pay_now: billingSummary.amountToPayNow,
        amount_to_pay_at_clinic: billingSummary.amountToPayAtClinic,
      },
    });
    setDisableBookingCreationButton(true);
    if (isAtHomeServiceSelected) {
      const locationPayload = getAtHomeLocationPayload() as {
        latitude?: number | null;
        longitude?: number | null;
        address?: string;
      };
      if (
        locationPayload?.latitude === null ||
        locationPayload?.longitude === null ||
        !`${locationPayload?.address ?? ""}`.trim()
      ) {
        setDisableBookingCreationButton(false);
        toast.error("Please select a valid address before booking.");
        return;
      }
    }
    if (rescheduleFlag) {
      const sm = params.get("source") ? params.get("source") : "appointment_module";
      let bookingRescheduleBody = constructBookingUpdateBody();
      if (lateBookingFlag) {
        bookingRescheduleBody = {
          ...bookingRescheduleBody,
          end_time: null,
          start_time: lateBookingStartTime,
          booking_type: bookingTypeLate,
          booking_start_date_time: `${filters?.booking_date} ${lateBookingStartTime}`,
          booking_end_date_time: null,
        };
      }
      updateBooking(
        { ...bookingRescheduleBody, source_module: sm as IBookingUpdateModuleType },
        rescheduledBooking?.booking_id,
      ).then(async (response) => {
        if (response.status) {
          await setDisableBookingCreationButton(false);
        } else {
          await setDisableBookingCreationButton(false);
        }
      });
    } else {
      const bookingCreationBody = constructBookingCreationBody(overbookingFlag);

      createBooking(bookingCreationBody).then(async (response) => {
        if (response.status) {
          await setDisableBookingCreationButton(false);
        } else {
          await setDisableBookingCreationButton(false);
        }
      });
    }
  };
  const handleMembershipPayAtClinicToggle = (checked: boolean) => {
    if (completedBookingFlow) return;
    dispatch(setSelectedPurchaseMembershipPayAtClinic(checked));

    const serviceCartItemId =
      previewServiceItem?.id ||
      selectedPurchaseServiceId ||
      purchaseOptionsData?.services?.[0]?.id ||
      filters?.sub_service_id;
    if (!customerId || !selectedSlotsClinicDetails?.clinic_id || !serviceCartItemId) return;

    fetchBookingCartPreview({
      user_id: customerId,
      pet_id: petId,
      clinic_id: selectedSlotsClinicDetails.clinic_id,
      cart_items: buildCartItemsPayload({ payAtClinic: checked, forCreate: false }),
      configuration_id: selectedSlotDetails?.configuration_id,
      ...surgeSlotPayload(selectedSlotDetails, filters?.booking_date, lateBookingFlag ? lateBookingStartTime : null),
      ...(rescheduleFlag && rescheduleBookingIdFromParams
        ? {
          is_reschedule: true,
          booking_id: rescheduleBookingIdFromParams,
        }
        : {}),
    });
  };

  useEffect(() => {
    if (!filters) {
      navigate(getRouteKey("AVAILABLE_SLOTS", "url"));
    }
  }, []);

  useEffect(() => {
    if (newBookingId && isFrontDeskOverbookingFlow) {
      logPreviewEvent("FRONT_DESK_OB_BOOKING_CREATED", {
        booking_id: newBookingId,
        filter_values: {
          booking_date: filters?.booking_date ?? null,
          start_time: selectedSlotDetails?.start_time ?? null,
          end_time: selectedSlotDetails?.end_time ?? null,
          provider_name: selectedSlotStaffDetails?.staff_name ?? null,
          service_type_id: filters?.service_type_id ?? null,
          sub_service_id: filters?.sub_service_id ?? null,
        },
      });
    }
  }, [newBookingId]);

  useEffect(() => {
    if (newBookingId) {
      setBookingCreated(true);
      handelRefesh();
    } else {
      setBookingCreated(false);
    }
  }, [newBookingId]);

  useEffect(() => {
    if (updatedBookingId) {
      handelRefesh();
      setBookingUpdated(true);
    } else {
      setBookingUpdated(false);
    }
  }, [updatedBookingId]);

  useEffect(() => {
    if (bookingCreated || bookingUpdated) {
      setCompleteBookingFlow(true);
    }
  }, [bookingCreated, bookingUpdated]);

  const appointmentDateLabel = ISODatetoHuman(filters?.booking_date) || "--";
  const slotStartTime = addAMPMToTime(lateBookingFlag ? lateBookingStartTime : selectedSlotDetails?.start_time) || "--";
  const slotEndTime = addAMPMToTime(selectedSlotDetails?.end_time) || "--";
  const slotTimeLabel = `${slotStartTime} - ${slotEndTime}`;
  const bookingFlowTitle = rescheduleFlag ? "Booking Rescheduled" : "Booking Confirmed";
  const bookingFlowSubtitle = rescheduleFlag
    ? "Your updated slot is locked and ready."
    : "Your appointment has been created successfully.";
  const availableSlotsUrl = `${getRouteKey("AVAILABLE_SLOTS", "url")}${params.toString() ? `?${params.toString()}` : ""
    }`;
  const availableSlotsAddressChangeUrl = (() => {
    const nextParams = new URLSearchParams(params);
    nextParams.set("open_address_list", "true");
    return `${getRouteKey("AVAILABLE_SLOTS", "url")}${nextParams.toString() ? `?${nextParams.toString()}` : ""}`;
  })();
  const isHomeServiceBooking =
    selectedServiceTypeId === HOME_SERVICE_ID ||
    filters?.service_type_id === HOME_SERVICE_ID ||
    params.get("service_type_id") === HOME_SERVICE_ID;
  const selectedVisitAddress =
    (address as any)?.state?.formattedAddress ||
    (address as any)?.title ||
    params.get("visit_address") ||
    params.get("address") ||
    "NA";
  const selectedAddressLabel = (address as any)?.label || params.get("address_label") || "Home";
  const wasLastVisitAddress =
    Boolean((address as any)?.previously_ordered) || params.get("last_visit")?.toLowerCase() === "true";
  const estimatedTravelTimeLabel = params.get("travel_time") || "~18 min.";
  const advancePaymentAmount = Number(selectedSlotDetails?.booking_fee ?? 0);
  const isBookingFeeWaived = Boolean(billingDetails?.is_booking_fee_waived);
  // A waived fee is zero payable, whatever the slot is configured at.
  //
  // The fallback chain below is the problem it guards: amount_to_pay_now
  // already excludes a waived fee, but booking_fee and slot.booking_fee are
  // the raw configured amount and carry no notion of the waiver. Whenever
  // amount_to_pay_now is absent the chain silently falls through to the full
  // fee, and the staff member is asked to collect money the backend is not
  // going to charge.
  const computedAdvancePayment = isBookingFeeWaived
    ? 0
    : Number(billingSummary.amountToPayNow ?? billingDetails?.booking_fee ?? selectedSlotDetails?.booking_fee ?? 0);
  const isPaidBooking = selectedSlotDetails?.prepaid_booking_slot?.toLowerCase() === "yes" && computedAdvancePayment > 0;
  const normalizedPaymentStatus = normalizePaymentStatus(bookingDetails, prepaidPayment);
  const paymentDisplayStatus = resolvePaymentDisplayStatus(bookingDetails, prepaidPayment);
  const isPaymentDisplayPaid = ["paid", "completed"].includes(paymentDisplayStatus?.toLowerCase());
  const isPrepaidPaymentCompleted =
    bookingDetails?.is_paid === true || ["paid", "completed"].includes(normalizedPaymentStatus);
  const isPrepaidPaymentPending = ["unpaid", "pending", "failed", "false", "0"].includes(normalizedPaymentStatus);
  const rescheduleMembershipStatus = String(summaryMembershipItem?.billing_status ?? "").toUpperCase();
  const isRescheduleMembershipBilled = rescheduleMembershipStatus === "BILLED";
  const isRescheduleMembershipUnbilled = rescheduleMembershipStatus === "UNBILLED";
  const isMembershipPaymentOnHold =
    completedBookingFlow &&
    (bookingUpdated || bookingCreated) &&
    !rescheduleFlag &&
    isPaidBooking &&
    !isBookingOnHold &&
    !isPrepaidPaymentCompleted;
  // Never ask for a fee the backend has waived. clinic_org carries the
  // original fee to the new slot for a staff-made late reschedule and issues
  // no new payment link, so collecting here charges a customer twice for one
  // booking. Gating on the amount alone missed that entirely, because the
  // waiver never reached this decision.
  const isReschedulePaymentPending =
    rescheduleFlag &&
    completedBookingFlow &&
    !isBookingFeeWaived &&
    !isRescheduleMembershipBilled &&
    !membershipPayAtClinicSelected &&
    computedAdvancePayment > 0 &&
    !isPrepaidPaymentCompleted;
  const showMembershipPayAtClinicToggle = !completedBookingFlow && (!rescheduleFlag || !summaryMembershipItem);
  return (
    <div className="bg-[#F3F4F6] p-2 pb-4">
      <div className="mx-auto w-full space-y-3">
        <div className="grid grid-cols-1 gap-2 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-2">
            <AppointmentDetailsCard
              completedBookingFlow={completedBookingFlow}
              availableSlotsUrl={availableSlotsUrl}
              bookingUpdated={bookingUpdated}
              bookingCreated={bookingCreated}
              isPaidBooking={isPaidBooking}
              isBookingOnHold={isBookingOnHold}
              isPrepaidPaymentCompleted={isPrepaidPaymentCompleted}
              customerDetails={customerDetails}
              petDetails={petDetails}
              bookingFlowTitle={bookingFlowTitle}
              bookingFlowSubtitle={bookingFlowSubtitle}
              selectedServiceTypeName={selectedServiceTypeName}
              selectedSubService={selectedSubService}
              slotTimeLabel={slotTimeLabel}
              appointmentDateLabel={appointmentDateLabel}
              selectedSlotStaffDetails={selectedSlotStaffDetails}
              selectedSlotsClinicDetails={selectedSlotsClinicDetails}
              normalizedUpsellScript={normalizedUpsellScript}
              logPreviewEvent={logPreviewEvent}
            />

            <AdvancePaymentCard
              bookingCreated={bookingCreated}
              bookingUpdated={bookingUpdated}
              selectedSlotDetails={selectedSlotDetails}
              prepaidPayment={prepaidPayment}
              isPaymentDisplayPaid={isPaymentDisplayPaid}
              paymentDisplayStatus={paymentDisplayStatus}
              customerDetails={customerDetails}
              refreshLoader={refreshLoader}
              logPreviewEvent={logPreviewEvent}
              setOpen={setOpen}
              handelRefesh={handelRefesh}
            />

            <CartItemsCard
              completedBookingFlow={completedBookingFlow}
              logPreviewEvent={logPreviewEvent}
              setEditCartModalSignal={setEditCartModalSignal}
              selectedMembershipItem={selectedMembershipItem}
              isMembershipPaymentOnHold={isMembershipPaymentOnHold}
              selectedMembershipCouponName={selectedMembershipCouponName}
              showMembershipPayAtClinicToggle={showMembershipPayAtClinicToggle}
              membershipPayAtClinicSelected={membershipPayAtClinicSelected}
              handleMembershipPayAtClinicToggle={handleMembershipPayAtClinicToggle}
              selectedMembershipFinalPrice={selectedMembershipFinalPrice}
              membershipDiscountPercent={membershipDiscountPercent}
              selectedMembershipOriginalPrice={selectedMembershipOriginalPrice}
              isRescheduleMembershipBilled={isRescheduleMembershipBilled}
              isPrepaidPaymentCompleted={isPrepaidPaymentCompleted}
              isRescheduleMembershipUnbilled={isRescheduleMembershipUnbilled}
              isReschedulePaymentPending={isReschedulePaymentPending}
              isPaidBooking={isPaidBooking}
              isPrepaidPaymentPending={isPrepaidPaymentPending}
              serviceDisplayItem={serviceDisplayItem}
              serviceOfferCallout={serviceOfferCallout}
              filters={filters}
              selectedVaccineDisplayItems={selectedVaccineDisplayItems}
              selectedAddOnItems={selectedAddOnItems}
              selectedDiagnosticItems={selectedDiagnosticItems}
              selectedSurgeryItems={selectedSurgeryItems}
              selectedServiceTypeName={selectedServiceTypeName}
            />

            {!completedBookingFlow && (
              <CommentSectionModal commentEdit={true} openOnSignal={editCartModalSignal} hideTrigger={true} isCommentModalEdit={true} />
            )}
          </div>

          <div className="space-y-2 xl:sticky xl:top-4 xl:self-start">
            <CommentsCard
              completedBookingFlow={completedBookingFlow}
              resolvedDiscountCouponLabel={resolvedDiscountCouponLabel}
              isHomeServiceBooking={isHomeServiceBooking}
              commentsData={commentsData}
              additionalCommentsList={additionalCommentsList}
            />

            <PricingSummaryCard
              billingSummary={billingSummary}
              isBookingFeeWaived={isBookingFeeWaived}
              advancePaymentAmount={advancePaymentAmount}
              isPaidBooking={isPaidBooking}
              completedBookingFlow={completedBookingFlow}
              bookingFeeChip={bookingFeeChip}
              bookingFeeChipText={bookingFeeChipText}
              isHomeServiceBooking={isHomeServiceBooking}
              selectedAddressLabel={selectedAddressLabel}
              wasLastVisitAddress={wasLastVisitAddress}
              selectedVisitAddress={selectedVisitAddress}
              estimatedTravelTimeLabel={estimatedTravelTimeLabel}
              availableSlotsAddressChangeUrl={availableSlotsAddressChangeUrl}
              disableBookingCreationButton={disableBookingCreationButton}
              rescheduleFlag={rescheduleFlag}
              customerId={customerId}
              petId={petId}
              logPreviewEvent={logPreviewEvent}
              handelConfirmation={handelConfirmation}
            />
          </div>
        </div>
      </div>
      {open && (
        <SendWhatsappScriptimport
          open={open}
          setOpen={setOpen}
          bookingId={rescheduleFlag ? updatedBookingId : newBookingId}
          petName={petDetails?.name}
        />
      )}
    </div>
  );
};

export default SlotsConfirmationComponent;
